import { useQuery } from "@apollo/client";
import { GetTenantQuery } from "generated-graphql/graphql";
import { isNil } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import useSessionStorageState from "use-session-storage-state";

import { gql } from "../generated-graphql";

gql(`
  fragment tenant on Tenant {
    id
    name
    isDisabled
    isDemo
    isPartner
    partnerTenantId
  }
`);

export const GET_TENANT_QUERY = gql(`
  query GetTenant($tenantId: ID!) {
    tenant(id: $tenantId) {
      ...tenant
      tenantConfigs {
        id
        tenantId
        key
        value
      }
    }
  }
`);

export type Tenant = GetTenantQuery["tenant"] | undefined;

const TENANT_SESSION_STORAGE_KEY = "savedTenant";

export function useTenantIdPathParam() {
  const pathParts = window.location.pathname.split("/");

  // Check for customer-facing route pattern: /o/{tenantId}
  const tenantIndex = pathParts.indexOf("o");
  if (tenantIndex !== -1) {
    return pathParts[tenantIndex + 1];
  }

  // Check for staff implementation route pattern: /staff/implementation/{tenantId}
  const implementationIndex = pathParts.indexOf("implementation");
  if (implementationIndex !== -1) {
    return pathParts[implementationIndex + 1];
  }

  // Check for staff SSE route pattern: /staff/sse/{tenantId}
  const sseIndex = pathParts.indexOf("sse");
  if (sseIndex !== -1 && pathParts[sseIndex - 1] === "staff") {
    return pathParts[sseIndex + 1];
  }

  return undefined;
}

/**
 * Returns the effective tenant id for the current context.
 * Prefers the tenant id from the URL path if present; otherwise falls back
 * to the session-stored selected tenant.
 */
export function useTenantId(): string | undefined {
  const pathTenantId = useTenantIdPathParam();
  const [savedTenant] = useSessionStorageState<Tenant>(TENANT_SESSION_STORAGE_KEY);
  return pathTenantId ?? savedTenant?.id;
}

export function useTenant() {
  const tenantIdPathParam = useTenantIdPathParam();

  const [savedTenant, setSavedTenant] = useSessionStorageState<Tenant>(TENANT_SESSION_STORAGE_KEY);

  const sessionTenantId = tenantIdPathParam ?? savedTenant?.id;

  const [internalTenantId, setInternalTenantId] = useState<string | undefined>(sessionTenantId);

  useEffect(() => {
    setInternalTenantId(sessionTenantId);
  }, [sessionTenantId]);

  const { data, loading, error } = useQuery(GET_TENANT_QUERY, {
    variables: {
      tenantId: internalTenantId ?? "",
    },
    skip: isNil(internalTenantId),
  });

  // Save tenant to session storage when query completes
  useEffect(() => {
    if (data?.tenant) {
      setSavedTenant(data.tenant);
    }
  }, [data?.tenant, setSavedTenant]);

  const setCurrentTenantId = useCallback(
    async (newTenantId: string | null | undefined) => {
      if (newTenantId && newTenantId !== internalTenantId) {
        setInternalTenantId(newTenantId);
      }
    },
    [internalTenantId]
  );

  const tenant = useMemo(() => {
    return data?.tenant ?? savedTenant;
  }, [data?.tenant, savedTenant]);

  const isAmazon = internalTenantId === "6a92e397-bb74-44ed-afee-f84c7821f40e";
  // HACK: LKQ wants "Products" labeled as "Products / Equipment"
  const isLKQ = internalTenantId === "d2981932-b600-4534-9c26-07f22a73fc0a";

  return {
    tenantId: internalTenantId,
    isAmazon,
    isLKQ,
    tenant: tenant?.id === internalTenantId ? tenant : undefined,
    loading,
    error,
    setCurrentTenantId,
  };
}
